import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stars, PerspectiveCamera } from '@react-three/drei';
import { EffectComposer, Bloom, ChromaticAberration } from '@react-three/postprocessing';
import { Vector2 } from 'three';
import Earth from './Earth';
import SolarSystem from './SolarSystem';
import SatelliteMarker from './SatelliteMarker';

const EarthViewer: React.FC = () => {
  return (
    <div className="fixed inset-0 w-screen h-screen bg-black">
      <Canvas
        gl={{ 
          antialias: true,
          alpha: false,
          powerPreference: "high-performance",
          stencil: false,
          depth: true
        }}
        dpr={[1, 2]}
        camera={{
          position: [0, 0, 5],
          fov: 45,
          near: 0.1,
          far: 2000
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at center, #0B1026 0%, #0F172A 50%, #000 100%)'
        }}
      >
        <Suspense fallback={null}>
          <PerspectiveCamera 
            makeDefault 
            position={[0, 0, 5]}
            fov={45}
          />
          
          {/* Lighting */}
          <ambientLight intensity={0.8} />
          <pointLight position={[100, 10, -50]} intensity={30} color="#4F46E5" />
          <pointLight position={[-100, -10, 50]} intensity={30} color="#00F5FF" />
          <pointLight position={[0, 0, 50]} intensity={30} color="#FFFFFF" />
          
          <Stars
            radius={500}
            depth={100}
            count={20000}
            factor={7}
            saturation={0}
            fade
            speed={1}
          />
          
          <Earth />
          <SolarSystem />
          <SatelliteMarker />
          
          <OrbitControls
            enablePan={false}
            enableDamping={true}
            dampingFactor={0.05}
            minDistance={1.5}
            maxDistance={200}
            rotateSpeed={0.5}
            zoomSpeed={1}
            minPolarAngle={Math.PI / 4}
            maxPolarAngle={Math.PI / 1.5}
          />

          <EffectComposer>
            <Bloom
              intensity={1.5}
              luminanceThreshold={0.2}
              luminanceSmoothing={0.9}
              mipmapBlur
              radius={0.7}
            />
            <ChromaticAberration
              offset={new Vector2(0.002, 0.002)}
              radialModulation={false}
              modulationOffset={0}
            />
          </EffectComposer>
        </Suspense>
      </Canvas>

      {/* Overlay gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#000]/50 via-transparent to-[#000]/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000]/50 via-transparent to-[#000]/50" />
      </div>

      {/* Scan lines effect */}
      <div className="absolute inset-0 pointer-events-none bg-scan-lines opacity-[0.03]" />
    </div>
  );
};

export default EarthViewer; 